import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Flex,
  Button,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { Form, Formik, ErrorMessage } from 'formik';
import React from 'react';

import {
  error as commonError,
  utils as commonUtils,
  date as commonDate,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  RailzIntegrationFragmentGQLType,
  useFetchAndUploadFinancialDataFromRailzMutation,
} from '../../requests/financialData.gql.generated';
import { colors } from 'packages/common-frontend/colors';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';

type FetchFinancialDataProps = {
  targetDate: Util.FullDateStr;
};

const FetchFinancialDataForm = ({
  railzIntegration,
  onClose,
}: {
  railzIntegration: RailzIntegrationFragmentGQLType;
  onClose: () => void;
}) => {
  const { id: railzIntegrationId, railzServiceName } = railzIntegration;
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const toast = useToast();

  const [
    fetchAndUploadFinancialData,
    { loading: isLoadingFetchData },
  ] = useFetchAndUploadFinancialDataFromRailzMutation({
    onCompleted: ({ dataCount }) => {
      if (dataCount > 0) {
        toast({
          title: `Successfully fetched data from ${commonUtils.toTitleCase(
            railzServiceName,
          )}.`,
          position: 'top',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: `Did not properly fetch any data from ${commonUtils.toTitleCase(
            railzServiceName,
          )}. Please contact support if this is unexpected and continues to occur.`,
          position: 'top',
          status: 'warning',
          duration: 7000,
          isClosable: true,
        });
      }
    },
    onError: (error) => {
      Sentry.captureException(
        new commonError.FinleyError(
          `Failed to manually fetch data from Railz for ${railzServiceName} [error:${error.message}]`,
          { metadata: { logMeta: { error, railzIntegration } } },
        ),
      );
      toast({
        title: `An error occurred while fetching data from ${commonUtils.toTitleCase(
          railzServiceName,
        )}. Please contact support@finleycms.com if this continues.`,
        position: 'top',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    },
  });

  const onSubmit = async (
    createParams: FetchFinancialDataProps,
  ): Promise<void> => {
    const { targetDate } = createParams;

    // Note that validation is already handled at the Formik level below
    const { errors } = await fetchAndUploadFinancialData({
      variables: {
        railzIntegrationId,
        queryParamInputs: {
          // Currently defaulting to "month" and "accrual" for user, as these
          // are our normal basis for data that we utilize and support.
          // This further simplifies UX, where user just need to select a
          // target date and then we can derive the respective monthly range.
          // Can revisit and expose these fields to be adjustable, if needed
          reportFrequency: 'month',
          accountingMethod: 'accrual',
          startDate: commonDate.firstOfPeriod(
            targetDate,
            Util.FrequencyPeriod.Month,
          ),
          endDate: commonDate.endOfPeriod(targetDate, 'month'),
        },
      },
    });
    if (errors) {
      toast({
        title: 'An error occurred while fetching financial data',
        description: errors.map((err) => err.message).join(', '),
        status: 'error',
        duration: 6000,
        position: 'top',
        isClosable: true,
      });
      return;
    }
    onClose();
  };

  return (
    <Formik<FetchFinancialDataProps>
      initialValues={{ targetDate: commonDate.currentDateStr() }}
      onSubmit={(props) => onSubmit(props)}
      validate={(values) => {
        const error: { [K in keyof typeof values]?: string } = {};

        if (!values.targetDate) {
          error.targetDate = 'Please select a date.';
        } else if (
          // Can't use future date to fetch data.
          // This technically is already gated by "maxDate" param on the
          // DatePicker, so this is just extra safety
          values.targetDate.localeCompare(commonDate.currentDateStr()) === 1
        ) {
          error.targetDate =
            'Future dates are not supported. Please select another date.';
        }

        return error;
      }}
      validateOnChange={false}
    >
      {(formProps) => (
        <Form onSubmit={formProps.handleSubmit}>
          {/* *** Target Date *** */}
          <Box>
            <Flex alignItems="center" justifyContent="space-between">
              <Text mr="3">Reporting Date</Text>
              <SimpleDatePicker
                width="50"
                tz={clientTimeZone}
                maxDate={new Date()}
                selectedDate={commonDate.fullDateStrToDate(
                  formProps.values.targetDate,
                )}
                setSelectedDate={(date: Date) => {
                  const fullDateStr = commonDate.dateToFullDateString(
                    date,
                    clientTimeZone,
                  );
                  formProps.setFieldValue('targetDate', fullDateStr);
                }}
              />
            </Flex>
            <ErrorMessage name="targetDate">
              {(msg) => (
                <Text textAlign="right" color={colors.error} pr="2" my="1">
                  {msg}
                </Text>
              )}
            </ErrorMessage>
          </Box>

          <Flex alignItems="center" justifyContent="flex-end" mt="5">
            <Button type="submit" mr="2" isLoading={isLoadingFetchData}>
              Fetch
            </Button>
            <Button onClick={onClose}>Cancel</Button>
          </Flex>
        </Form>
      )}
    </Formik>
  );
};

export const RailzFetchFinancialDataModal = ({
  isOpen,
  onClose,
  railzIntegration,
}: {
  isOpen: boolean;
  onClose: () => void;
  railzIntegration: RailzIntegrationFragmentGQLType;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent p="3">
        <ModalHeader>Fetch Financial Data</ModalHeader>
        <ModalCloseButton />
        <ModalBody w="100%">
          <FetchFinancialDataForm
            railzIntegration={railzIntegration}
            onClose={onClose}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
